import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';

export interface UserSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  marketingEmails: boolean;
  invoiceReminders: boolean;
  serviceAlerts: boolean;
  serviceRemindersEnabled: boolean;
  reminderTime: string;
  autoSave: boolean;
  darkMode: boolean;
  timezone: string;
  dateFormat: string;
  currency: string;
  language: string;
  twoFactorAuth: boolean;
  showDemoData: boolean;
}

export const defaultSettings: UserSettings = {
  emailNotifications: true,
  pushNotifications: false,
  smsNotifications: false,
  marketingEmails: true,
  invoiceReminders: true,
  serviceAlerts: true,
  serviceRemindersEnabled: false,
  reminderTime: '13:00',
  autoSave: true,
  darkMode: false,
  timezone: 'Europe/Madrid',
  dateFormat: 'DD/MM/YYYY',
  currency: 'EUR',
  language: 'es',
  twoFactorAuth: false,
  showDemoData: false
};

export const useSettings = () => {
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { token, isAuthenticated } = useAuth();

  // Cargar ajustes del servidor
  const loadSettings = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(buildApiUrl('/api/settings'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load settings');
      }

      const data = await response.json();
      
      // Combinar ajustes del servidor con valores por defecto
      const serverSettings = data.data || {};
      const mergedSettings = { ...defaultSettings, ...serverSettings };
      
      setSettings(mergedSettings);
    } catch (err: any) {
      console.error('Error loading settings:', err);
      setError(err.message);
      // En caso de error, usar ajustes por defecto
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  // Guardar ajustes en el servidor
  const saveSettings = useCallback(async (newSettings: Partial<UserSettings>) => {
    if (!isAuthenticated || !token) {
      throw new Error('Not authenticated');
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(buildApiUrl('/api/settings'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      // Actualizar estado local
      setSettings(prev => ({ ...prev, ...newSettings }));
      
      return true;
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [isAuthenticated, token]);

  // Actualizar un ajuste específico
  const updateSetting = useCallback(async <K extends keyof UserSettings>(
    key: K, 
    value: UserSettings[K]
  ) => {
    try {
      await saveSettings({ [key]: value });
    } catch (err) {
      // El error ya se maneja en saveSettings
      throw err;
    }
  }, [saveSettings]);

  // Actualizar múltiples ajustes
  const updateSettings = useCallback(async (newSettings: Partial<UserSettings>) => {
    try {
      await saveSettings(newSettings);
    } catch (err) {
      // El error ya se maneja en saveSettings
      throw err;
    }
  }, [saveSettings]);

  // Resetear ajustes a valores por defecto
  const resetSettings = useCallback(async () => {
    try {
      await saveSettings(defaultSettings);
    } catch (err) {
      throw err;
    }
  }, [saveSettings]);

  // Inicializar ajustes por defecto en el servidor
  const initializeSettings = useCallback(async () => {
    if (!isAuthenticated || !token) {
      throw new Error('Not authenticated');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildApiUrl('/api/settings/initialize'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to initialize settings');
      }

      // Recargar ajustes después de inicializar
      await loadSettings();
      
      return true;
    } catch (err: any) {
      console.error('Error initializing settings:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, token, loadSettings]);

  // Cargar ajustes al montar el componente
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return {
    settings,
    loading,
    error,
    saving,
    updateSetting,
    updateSettings,
    saveSettings,
    resetSettings,
    initializeSettings,
    loadSettings
  };
};